import { fetchAuthSession } from 'aws-amplify/auth';

const API_URL = process.env.REACT_APP_API_URL || '';

async function getToken(): Promise<string> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || '';
  } catch {
    return '';
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = await getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) => request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) => request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

// User-session activity logging (login / logout / search / view). Fire-and-forget:
// never throws, never blocks the UI. Records to the audit trail server-side.
export function logActivity(action: 'login' | 'logout' | 'search' | 'view' | 'export' | 'heartbeat', opts: { query?: string; page?: string; detail?: string } = {}): void {
  try {
    api.post('/activity', { action, ...opts }).catch(() => {});
  } catch { /* ignore */ }
}

// ═══════════════════════════════════════════════════════════════
// API paths aligned to fti-simple-api backend routes:
//   GET  /opportunities          → list
//   GET  /opportunities/:id      → get
//   POST /opportunities          → create
//   GET  /opportunities/:id/analyze   → run analysis
//   GET  /opportunities/:id/matches   → get vendor matches
//   GET  /opportunities/:id/deep-analysis?vendor_id=x → deep analysis
//   POST /opportunities/:id/evaluate  → evaluate vendors
//   GET  /vendors                → list
//   POST /vendors                → create
//   POST /documents              → upload
//   GET  /documents              → list
// ═══════════════════════════════════════════════════════════════

// Buyside lens: the caller's own profile (org_type buyer|seller) + Bid/No-Bid.
export const profileApi = {
  get: () => api.get<any>('/profile'),
  update: (data: { org_type?: 'buyer' | 'seller'; display_name?: string }) =>
    api.put<any>('/profile', data),
};

export const bidNoBidApi = {
  /** Grounded Bid/No-Bid analysis. Pass an opportunity_id OR naics_code (+agency, set_aside_code). */
  analyze: (data: { opportunity_id?: string; naics_code?: string; agency?: string; set_aside_code?: string; title?: string }) =>
    api.post<any>('/bid-no-bid', data),
};

// Seller pursuit workspace (Phase 2): Capture Plan + Compliance Matrix.
export const pursuitApi = {
  getCapturePlan: (oppId: string) => api.get<any>(`/pursuit/capture-plan/${oppId}`),
  /** AI-draft the capture plan; pass {save:true} to persist. */
  generateCapturePlan: (oppId: string, save = false) =>
    api.post<any>(`/pursuit/capture-plan/${oppId}`, { save }),
  getComplianceMatrix: (oppId: string) => api.get<any>(`/pursuit/compliance-matrix/${oppId}`),
  /** AI-shred uploaded solicitation docs into requirement rows. */
  shredComplianceMatrix: (oppId: string) =>
    api.post<any>(`/pursuit/compliance-matrix/${oppId}`, { action: 'shred' }),
  /** Persist the (edited) matrix rows. */
  saveComplianceMatrix: (oppId: string, rows: any[]) =>
    api.post<any>(`/pursuit/compliance-matrix/${oppId}`, { action: 'save', rows }),
  /** Proposal Response: get a volume's section list (no AI, instant). */
  getProposalSections: (oppId: string, volume: string) =>
    api.post<any>(`/pursuit/proposal-draft/${oppId}`, { volume }),
  /** Proposal Response: draft ONE section (fast; client loops sections). */
  draftProposalSection: (oppId: string, volume: string, sectionIndex: number) =>
    api.post<any>(`/pursuit/proposal-draft/${oppId}`, { volume, section_index: sectionIndex }),
  /** Self-Score: grade the drafted proposal (uses saved proposal docs, or pass {text}). */
  selfScore: (oppId: string, text?: string) =>
    api.post<any>(`/pursuit/self-score/${oppId}`, text ? { text } : {}),
  /** Award/Debrief tracking. */
  getAward: (oppId: string) => api.get<any>(`/pursuit/award/${oppId}`),
  saveAward: (oppId: string, data: { outcome: string; award_amount?: string; awardee?: string; debrief_requested?: boolean; debrief_notes?: string }) =>
    api.post<any>(`/pursuit/award/${oppId}`, data),
};

// Phase 3.0: Vendor Capability Profile + on-demand opportunity match ("Match Me").
export const vendorProfileApi = {
  /** Extract an advisory capability profile from any combination of pasted text,
   *  a website URL, and a SAM registration (vendor_id). */
  extract: (input: { text?: string; url?: string; vendor_id?: string }) =>
    api.post<any>('/vendor-profile/extract', input),
  /** Look up the seller's own SAM registration by name / UEI / CAGE. */
  samLookup: (q: string) => api.get<any>(`/vendor-profile/sam-lookup?q=${encodeURIComponent(q)}`),
  /** The caller's saved capability profile (or empty). */
  get: () => api.get<any>('/vendor-profile'),
  /** Save the caller's (edited) capability profile; sets org_type=seller if unset. */
  save: (profile: any) => api.put<any>('/vendor-profile', { profile }),
  /** Rank active opportunities for the saved profile (or a passed one). */
  matchOpportunities: (profile?: any) =>
    api.post<any>('/vendor-profile/opportunities', profile ? { profile } : {}),
  /** Teach the matcher: mark a ranked opportunity relevant or not (learned for next time). */
  matchFeedback: (data: { notice_id?: string; title?: string; naics?: string; psc?: string; agency?: string; relevant: boolean; note?: string }) =>
    api.post<any>('/vendor-profile/match-feedback', data),
};

// Admin: AWS usage + daily AI cost cap.
export const aiUsageApi = {
  get: () => api.get<any>('/ai-usage'),
  setCap: (daily_cap_usd: number) => api.post<any>('/ai-usage/cap', { daily_cap_usd }),
};

export const opportunitiesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return api.get<any>(`/opportunities${qs}`);
  },
  get: (id: string) => api.get<any>(`/opportunities/${id}`),
  create: (data: any) => api.post<any>('/opportunities', data),
  update: (id: string, data: any) => api.put<any>(`/opportunities/${id}`, data),
  delete: (id: string) => api.del<any>(`/opportunities/${id}`),
  deepAnalysis: (opportunityId: string, vendorId: string) =>
    api.get<any>(`/opportunities/${opportunityId}/deep-analysis?vendor_id=${vendorId}`),
  /** Applicable regulations (FAR parts per lifecycle stage + the org supplement),
   *  with the resolved contracting organization and selectable override options. */
  getRegulations: (id: string) => api.get<any>(`/opportunities/${id}/regulations`),
  /** Governing FAR threshold classification (micro/SAT/commercial-SAT/above + triggers). */
  thresholds: (id: string) => api.get<any>(`/opportunities/${id}/thresholds`),
  /** AI-suggested (advisory) FAR + agency-supplement clauses for this opportunity. */
  suggestClauses: (id: string) => api.post<any>(`/opportunities/${id}/suggest-clauses`, {}),

  /** Contracting-organization options (label + FAR/supplement stack) for the create form dropdown. */
  orgOptions: () => api.get<any>('/opportunities/org-options'),
};

export const analysisApi = {
  run: (opportunityId: string) => api.get<any>(`/opportunities/${opportunityId}/analyze`),
  get: (id: string) => api.get<any>(`/opportunities/${id}`),
};

export const vendorsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return api.get<any>(`/vendors${qs}`);
  },
  get: (id: string) => api.get<any>(`/vendors/${id}`),
  getAwards: (id: string, limit = 100) => api.get<any>(`/vendors/${id}/awards?limit=${limit}`),
  getSubawards: (id: string, limit = 200) => api.get<any>(`/vendors/${id}/subawards?limit=${limit}`),
  triggerMatching: (opportunityId: string, _analysisRunId?: string) =>
    api.get<any>(`/opportunities/${opportunityId}/matches`),
  listMatches: (params?: Record<string, string>) => {
    // For fti-simple, matches are returned directly from triggerMatching
    // This is a fallback that returns empty if called separately
    return Promise.resolve({ items: [] });
  },
  recordDecision: (vendorMatchId: string, decision: string, reason: string) =>
    api.put<any>('/vendors', { vendor_match_id: vendorMatchId, decision, reason }),
  uploadCsv: (csvData: string) => api.post<any>('/vendors/csv-upload', { csv_data: csvData }),
  /** Start the background SAM monthly .dat import from an S3 key (admin). */
  importSam: (s3Key: string) => api.post<any>('/vendors/import-sam', { s3_key: s3Key }),
  /** Poll SAM import job progress. */
  importSamStatus: () => api.get<any>('/vendors/import-sam/status'),
  uploadCsvChunk: (csvChunk: string, chunkIndex: number, totalChunks: number) =>
    api.post<any>('/vendors/csv-upload', { csv_data: csvChunk, chunk_index: chunkIndex, total_chunks: totalChunks, is_chunked: true }),
  ingestKnowledgeBase: () => api.post<any>('/vendors/ingest-knowledge-base', {}),
};

export const matchesApi = {
  list: (_params?: Record<string, string>) => Promise.resolve({ items: [] }),
  decide: (_matchId: string, _decision: string, _reason: string) => Promise.resolve({}),
};

// Price Benchmark — precomputed "what agencies paid" stats by NAICS/PSC.
export const priceBenchmarkApi = {
  get: (opts: { naics?: string; psc?: string; from?: string; to?: string }) => {
    const p = new URLSearchParams();
    if (opts.naics) p.set('naics', opts.naics);
    if (opts.psc) p.set('psc', opts.psc);
    if (opts.from) p.set('from', opts.from);
    if (opts.to) p.set('to', opts.to);
    return api.get<any>(`/price-benchmark?${p.toString()}`);
  },
  // AI cost estimate grounded in the award benchmark for a described requirement.
  estimate: (opts: { description: string; naics?: string; psc?: string }) =>
    api.post<any>('/price-benchmark/estimate', {
      description: opts.description,
      naics: opts.naics || '',
      psc: opts.psc || '',
    }),
};

export const reportsApi = {
  generate: (_analysisId: string) => Promise.resolve({}),
  list: () => Promise.resolve({ items: [] }),
  get: (_id: string) => Promise.resolve({}),
};

export const adminApi = {
  getIngestionStatus: () => Promise.resolve({ totals: {}, status_summary: {} }),
  triggerIngestion: (_mode: string) => Promise.resolve({}),
  listUsers: () => Promise.resolve({ items: [] }),
  createUser: (_email: string, _role: string) => Promise.resolve({}),
  runMigrations: () => Promise.resolve({}),
};

export const vendorEvaluationApi = {
  upload: (opportunityId: string, vendorId: string, file: File, notes?: string) => {
    return new Promise<any>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = (reader.result as string).split(',')[1];
        try {
          const result = await api.post<any>(`/opportunities/${opportunityId}/evaluate`, {
            action: 'upload',
            vendor_id: vendorId,
            filename: file.name,
            file_content: base64,
            file_type: file.name.split('.').pop()?.toLowerCase() || 'pdf',
            notes: notes || '',
          });
          resolve(result);
        } catch (e) {
          reject(e);
        }
      };
      reader.onerror = () => reject(new Error('File read error'));
      reader.readAsDataURL(file);
    });
  },
  addNotes: (opportunityId: string, vendorId: string, notes: string) =>
    api.post<any>(`/opportunities/${opportunityId}/evaluate`, {
      action: 'upload',
      vendor_id: vendorId,
      notes,
    }),
  analyze: (opportunityId: string, vendorId: string, notes?: string) =>
    api.post<any>(`/opportunities/${opportunityId}/evaluate`, {
      action: 'analyze',
      vendor_id: vendorId,
      notes: notes || '',
    }),
  get: (opportunityId: string, vendorId: string) =>
    api.get<any>(`/opportunities/${opportunityId}/evaluate?vendor_id=${vendorId}`).catch(() => ({ documents: [], evaluation: null })),
  list: (opportunityId: string) =>
    api.get<any>(`/opportunities/${opportunityId}/evaluate`).catch(() => ({ items: [] })),
  deleteDocument: (documentId: string) =>
    api.del<any>(`/documents/${documentId}`),
};

export const documentsApi = {
  upload: (opportunityId: string, file: File, category?: string) => {
    return new Promise<any>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = (reader.result as string).split(',')[1];
        try {
          const result = await api.post<any>('/documents', {
            opportunity_id: opportunityId,
            filename: file.name,
            file_content: base64,
            file_type: file.name.split('.').pop()?.toLowerCase() || 'pdf',
            ...(category ? { category } : {}),
          });
          resolve(result);
        } catch (e) {
          reject(e);
        }
      };
      reader.onerror = () => reject(new Error('File read error'));
      reader.readAsDataURL(file);
    });
  },
  get: (docId: string) => api.get<any>(`/documents/${docId}`),
  /** Delete a document (file + record). Requires delete authority. */
  remove: (docId: string) => api.del<any>(`/documents/${docId}`),
  /** Edit a text-based document's content; re-renders the file in its format. */
  updateText: (docId: string, text: string) => api.post<any>(`/documents/${docId}/update-text`, { text }),
  /** Get a presigned download URL for any stored document (uploaded or generated). */
  downloadUrl: (docId: string) => api.get<any>(`/documents/${docId}/download`),
  /** Get the concatenated text of an opportunity's documents (uploaded + AI-generated),
   *  bounded, so the draft generator can ground new documents in existing ones. */
  context: (opportunityId: string, budget?: number) =>
    api.get<any>(`/documents/context?opportunity_id=${encodeURIComponent(opportunityId)}${budget ? `&budget=${budget}` : ''}`),
  /** Persist an AI-generated draft as a first-class, downloadable document on the
   *  opportunity (appears in the Documents panel + feeds AI context). Format is
   *  'pdf' | 'docx' | 'txt' (defaults to pdf server-side). */
  saveDraft: (opportunityId: string, documentType: string, title: string, text: string, format: string = 'pdf',
              meta?: { opportunity?: string; procurement_case?: string; agency?: string; far?: string }) =>
    api.post<any>('/documents/save-draft', {
      opportunity_id: opportunityId, document_type: documentType, title, text, format,
      ...(meta || {}),
    }),
};

export const solicitationAnalysisApi = {
  /** Run a new solicitation analysis on an opportunity's uploaded documents */
  run: (opportunityId: string) =>
    api.post<any>('/v1/solicitation-analysis', { opportunity_id: opportunityId }),
  /** Get a specific analysis by its ID */
  get: (analysisId: string) =>
    api.get<any>(`/v1/solicitation-analysis/${analysisId}`),
  /** Get the latest analysis for an opportunity */
  getLatest: (opportunityId: string) =>
    api.get<any>(`/v1/solicitation-analysis?opportunity_id=${opportunityId}`),
};

// ═══════════════════════════════════════════════════════════════
// Receipt / Review / Acceptance (RRA) API
// ═══════════════════════════════════════════════════════════════

export const rraApi = {
  listReceipts: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return api.get<any>(`/rra/receipts${qs}`);
  },
  listReviews: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return api.get<any>(`/rra/reviews${qs}`);
  },
  listAcceptances: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return api.get<any>(`/rra/acceptances${qs}`);
  },
  getSummary: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return api.get<any>(`/rra/summary${qs}`);
  },
};


// ═══════════════════════════════════════════════════════════════
// SAM.gov Library API
// ═══════════════════════════════════════════════════════════════

export const samLibraryApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return api.get<any>(`/sam-library${qs}`);
  },
  get: (id: string) => api.get<any>(`/sam-library/${id}`),
  delete: (id: string) => api.del<any>(`/sam-library/${id}`),
  deleteAll: () => api.del<any>('/sam-library/all'),
  uploadCsv: (csvData: string, chunkIndex?: number, totalChunks?: number) =>
    api.post<any>('/sam-library/csv-upload', {
      csv_data: csvData,
      chunk_index: chunkIndex || 0,
      total_chunks: totalChunks || 1,
      is_chunked: (totalChunks || 1) > 1,
    }),
};


// ═══════════════════════════════════════════════════════════════
// Awards Database API
// ═══════════════════════════════════════════════════════════════

export const awardsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return api.get<any>(`/awards${qs}`);
  },
  get: (id: string) => api.get<any>(`/awards/${id}`),
  create: (data: any) => api.post<any>('/awards', data),
  update: (id: string, data: any) => api.put<any>(`/awards/${id}`, data),
  delete: (id: string) => request<any>(`/awards/${id}`, { method: 'DELETE' }),
  uploadCsv: (csvData: string) => api.post<any>('/awards/csv-upload', { csv_data: csvData }),
  extractVendors: (awardIds?: string[]) => api.post<any>('/awards/extract-vendors', { award_ids: awardIds || [] }),
};


// ═══════════════════════════════════════════════════════════════
// Industry Engagement Portal API
// ═══════════════════════════════════════════════════════════════

export const engagementApi = {
  // Engagements (Vendor Interactions)
  listEngagements: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return api.get<any>(`/engagements${qs}`);
  },
  getEngagement: (id: string) => api.get<any>(`/engagements/${id}`),
  createEngagement: (data: any) => api.post<any>('/engagements', data),
  updateEngagement: (id: string, data: any) => api.put<any>(`/engagements/${id}`, data),

  // Action Items
  listActionItems: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return api.get<any>(`/action-items${qs}`);
  },
  createActionItem: (data: any) => api.post<any>('/action-items', data),
  updateActionItem: (id: string, data: any) => api.put<any>(`/action-items/${id}`, data),
  deleteActionItem: (id: string) => api.post<any>(`/action-items/${id}`, { _method: 'DELETE' }),

  // Messages
  listMessages: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return api.get<any>(`/messages${qs}`);
  },
  sendMessage: (data: any) => api.post<any>('/messages', data),
  generateAiMessage: (data: any) => api.post<any>('/messages', { action: 'generate', ...data }),

  // Events (Calendar)
  listEvents: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return api.get<any>(`/events${qs}`);
  },
  getEvent: (id: string) => api.get<any>(`/events/${id}`),
  createEvent: (data: any) => api.post<any>('/events', data),
  updateEvent: (id: string, data: any) => api.put<any>(`/events/${id}`, data),
  deleteEvent: (id: string) => api.post<any>(`/events/${id}`, { _method: 'DELETE' }),

  // Analytics
  getAnalytics: () => api.get<any>('/engagement-analytics'),

  // Email Settings
  getEmailSettings: () => api.get<any>('/email-settings'),
  saveEmailSettings: (data: any) => api.post<any>('/email-settings', data),
};


// ═══════════════════════════════════════════════════════════════
// Global Notifications API — callable from any tab/page
// ═══════════════════════════════════════════════════════════════

export const notificationsApi = {
  /** List all alerts/notifications */
  list: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return api.get<any>(`/alerts${qs}`);
  },

  /** Create a new notification/alert from anywhere in the system */
  create: (data: {
    title: string;
    description?: string;
    type?: 'info' | 'warning' | 'success' | 'error' | 'deadline' | 'action_required';
    source?: string;          // which tab/module created it (e.g., "lifecycle", "opportunities", "vendors")
    source_id?: string;       // ID of the related entity
    priority?: 'low' | 'medium' | 'high' | 'critical';
    action_url?: string;      // deep link to the relevant page
    expires_at?: string;      // ISO date when this notification expires
  }) => api.post<any>('/alerts', {
    ...data,
    match_type: data.type || 'info',
    status: 'active',
  }),

  /** Dismiss/acknowledge a notification */
  dismiss: (id: string) => api.post<any>(`/alerts/${id}/dismiss`, {}),

  /** Mark all as read */
  dismissAll: () => api.post<any>('/alerts/dismiss-all', {}),

  /** Get count of active (unread) notifications */
  getUnreadCount: () => api.get<any>('/alerts?status=active&count_only=true').catch(() => ({ count: 0 })),
};

/**
 * Quick helper to fire a notification from any page.
 * Usage:
 *   import { notify } from '../api/client';
 *   notify('Lifecycle advanced to Award', { type: 'success', source: 'lifecycle', source_id: lifecycleId });
 *   notify('Deadline in 3 days: RFP Response', { type: 'deadline', priority: 'high', action_url: '/lifecycle' });
 */
export async function notify(
  title: string,
  options?: {
    description?: string;
    type?: 'info' | 'warning' | 'success' | 'error' | 'deadline' | 'action_required';
    source?: string;
    source_id?: string;
    priority?: 'low' | 'medium' | 'high' | 'critical';
    action_url?: string;
    expires_at?: string;
  }
): Promise<void> {
  try {
    await notificationsApi.create({ title, ...options });
  } catch (err) {
    console.warn('Notification send failed (non-blocking):', err);
  }
}

// ═══════════════════════════════════════════════════════════════
// Solicitation API
// ═══════════════════════════════════════════════════════════════

export const solicitationsApi = {
  list: (params?: Record<string, string>) => { const qs = params ? '?' + new URLSearchParams(params).toString() : ''; return api.get<any>(`/solicitations${qs}`); },
  get: (id: string) => api.get<any>(`/solicitations/${id}`),
  create: (data: any) => api.post<any>('/solicitations', data),
  update: (id: string, data: any) => api.put<any>(`/solicitations/${id}`, data),
  addClin: (id: string, data: any) => api.post<any>(`/solicitations/${id}/clins`, data),
  getClins: (id: string) => api.get<any>(`/solicitations/${id}/clins`),
  addClause: (id: string, data: any) => api.post<any>(`/solicitations/${id}/clauses`, data),
  getClauses: (id: string) => api.get<any>(`/solicitations/${id}/clauses`),
  addQA: (id: string, data: any) => api.post<any>(`/solicitations/${id}/qa`, data),
  getQA: (id: string) => api.get<any>(`/solicitations/${id}/qa`),
  publish: (id: string, data: any) => api.post<any>(`/solicitations/${id}/publish`, data),
};

// ═══════════════════════════════════════════════════════════════
// Formal Evaluation API
// ═══════════════════════════════════════════════════════════════

export const formalEvaluationsApi = {
  list: (params?: Record<string, string>) => { const qs = params ? '?' + new URLSearchParams(params).toString() : ''; return api.get<any>(`/formal-evaluations${qs}`); },
  get: (id: string) => api.get<any>(`/formal-evaluations/${id}`),
  create: (data: any) => api.post<any>('/formal-evaluations', data),
  addFactor: (id: string, data: any) => api.post<any>(`/formal-evaluations/${id}/factors`, data),
  getFactors: (id: string) => api.get<any>(`/formal-evaluations/${id}/factors`),
  assignEvaluator: (id: string, data: any) => api.post<any>(`/formal-evaluations/${id}/assign`, data),
  addObservation: (id: string, data: any) => api.post<any>(`/formal-evaluations/${id}/observation`, data),
  aiAssist: (id: string, data: any) => api.post<any>(`/formal-evaluations/${id}/ai-assist`, data),
  recordConsensus: (id: string, data: any) => api.post<any>(`/formal-evaluations/${id}/consensus`, data),
};

// ═══════════════════════════════════════════════════════════════
// Award Decisions & Contracts API
// ═══════════════════════════════════════════════════════════════

export const awardDecisionsApi = {
  list: (params?: Record<string, string>) => { const qs = params ? '?' + new URLSearchParams(params).toString() : ''; return api.get<any>(`/award-decisions${qs}`); },
  get: (id: string) => api.get<any>(`/award-decisions/${id}`),
  create: (data: any) => api.post<any>('/award-decisions', data),
  update: (id: string, data: any) => api.put<any>(`/award-decisions/${id}`, data),
  approve: (id: string) => api.post<any>(`/award-decisions/${id}/approve`, {}),
};

export const contractsApi = {
  list: (params?: Record<string, string>) => { const qs = params ? '?' + new URLSearchParams(params).toString() : ''; return api.get<any>(`/contracts${qs}`); },
  get: (id: string) => api.get<any>(`/contracts/${id}`),
  create: (data: any) => api.post<any>('/contracts', data),
  update: (id: string, data: any) => api.put<any>(`/contracts/${id}`, data),
  listModifications: (id: string) => api.get<any>(`/contracts/${id}/modifications`),
  addModification: (id: string, data: any) => api.post<any>(`/contracts/${id}/modifications`, data),
  listCorActivities: (id: string) => api.get<any>(`/contracts/${id}/cor-activities`),
  addCorActivity: (id: string, data: any) => api.post<any>(`/contracts/${id}/cor-activities`, data),
  getCloseout: (id: string) => api.get<any>(`/contracts/${id}/closeout`),
  updateCloseout: (id: string, data: any) => api.post<any>(`/contracts/${id}/closeout`, data),
};

// ═══════════════════════════════════════════════════════════════
// AI Governance & Security Readiness API
// ═══════════════════════════════════════════════════════════════

export const aiGovernanceApi = {
  inventory: () => api.get<any>('/ai-governance/inventory'),
  list: (params?: Record<string, string>) => { const qs = params ? '?' + new URLSearchParams(params).toString() : ''; return api.get<any>(`/ai-governance${qs}`); },
  get: (id: string) => api.get<any>(`/ai-governance/${id}`),
  create: (data: any) => api.post<any>('/ai-governance', data),
  update: (id: string, data: any) => api.put<any>(`/ai-governance/${id}`, data),
  listFeedback: (params?: Record<string, string>) => { const qs = params ? '?' + new URLSearchParams(params).toString() : ''; return api.get<any>(`/ai-governance/feedback${qs}`); },
  submitFeedback: (data: any) => api.post<any>('/ai-governance/feedback', data),
};

export const securityApi = {
  controls: () => api.get<any>('/security-readiness'),
  summary: () => api.get<any>('/security-readiness/summary'),
  updateControl: (data: any) => api.post<any>('/security-readiness', data),
};

export const accessibilityApi = {
  getEvidence: () => api.get<any>('/accessibility-evidence'),
  addEvidence: (data: any) => api.post<any>('/accessibility-evidence', data),
};

// ═══════════════════════════════════════════════════════════════
// Strategies API
// ═══════════════════════════════════════════════════════════════

export const strategiesApi = {
  list: (params?: Record<string, string>) => { const qs = params ? '?' + new URLSearchParams(params).toString() : ''; return api.get<any>(`/strategies${qs}`); },
  get: (id: string) => api.get<any>(`/strategies/${id}`),
  create: (data: any) => api.post<any>('/strategies', data),
  update: (id: string, data: any) => api.put<any>(`/strategies/${id}`, data),
  addComment: (id: string, section: string, comment: string) => api.post<any>(`/strategies/${id}/comment`, { section, comment }),
  submit: (id: string) => api.post<any>(`/strategies/${id}/submit`, {}),
  approve: (id: string, decision: string, comments: string) => api.post<any>(`/strategies/${id}/approve`, { decision, comments }),
  aiRecommend: (id: string) => api.get<any>(`/strategies/${id}/ai-recommend`),
};

// ═══════════════════════════════════════════════════════════════
// Procurement Cases API
// ═══════════════════════════════════════════════════════════════

export const casesApi = {
  list: (params?: Record<string, string>) => { const qs = params ? '?' + new URLSearchParams(params).toString() : ''; return api.get<any>(`/procurement-cases${qs}`); },
  get: (id: string) => api.get<any>(`/procurement-cases/${id}`),
  create: (data: any) => api.post<any>('/procurement-cases', data),
  update: (id: string, data: any) => api.put<any>(`/procurement-cases/${id}`, data),
  delete: (id: string) => api.del<any>(`/procurement-cases/${id}`),
  advance: (id: string, data?: any) => api.post<any>(`/procurement-cases/${id}/advance`, data || {}),
  listVendors: (id: string) => api.get<any>(`/procurement-cases/${id}/vendors`),
  addVendor: (id: string, data: any) => api.post<any>(`/procurement-cases/${id}/vendors`, data),
  updateVendor: (id: string, vid: string, data: any) => api.put<any>(`/procurement-cases/${id}/vendors/${vid}`, data),
};

// ═══════════════════════════════════════════════════════════════
// Auth API
// ═══════════════════════════════════════════════════════════════

export const auditApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return api.get<any>(`/audit${qs}`);
  },
  /** Per-user time-in-system reconstructed from presence/heartbeat events. */
  timeSummary: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return api.get<any>(`/audit/time-summary${qs}`);
  },
};

export const matchTrainingApi = {
  list: () => api.get<any>('/match-training'),
  save: (data: any) => api.post<any>('/match-training', data),
  runMatch: (data: any) => api.post<any>('/match-training/run', data),
  runGeneration: (data: any) => api.post<any>('/match-training/run-generation', data),
  remove: (id: string) => api.del<any>(`/match-training/${id}`),
  /** Known contracting organizations (label + regulatory stack) for the training scope. */
  orgOptions: () => api.get<any>('/match-training/org-options'),
  /** Train a model from real government-authored files (SAM.gov notices,
   *  solicitations, evaluations). Uploads ONE file per request (each base64 file
   *  can be several MB and API Gateway caps a request at 10MB, so batching many
   *  files in one POST fails with "Failed to fetch"). Results are aggregated. */
  uploadFiles: async (targetModel: string, files: File[], extra?: { naics?: string; psc?: string }) => {
    const toBase64 = (file: File) =>
      new Promise<{ filename: string; file_content: string; file_type: string }>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () =>
          resolve({
            filename: file.name,
            file_content: (reader.result as string).split(',')[1],
            file_type: file.name.split('.').pop()?.toLowerCase() || 'pdf',
          });
        reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
        reader.readAsDataURL(file);
      });

    const results: any[] = [];
    let queued = 0;
    for (const file of files) {
      try {
        const encoded = await toBase64(file);
        const res = await api.post<any>('/match-training/upload-files', {
          target_model: targetModel,
          files: [encoded],
          naics: extra?.naics || '',
          psc: extra?.psc || '',
        });
        const r = (res.results && res.results[0]) || { filename: file.name, status: 'processing' };
        results.push(r);
        if (r.status === 'processing' || r.status === 'ok') queued += 1;
      } catch (e: any) {
        results.push({ filename: file.name, status: 'error', error: e?.message || 'upload failed (file may be too large)' });
      }
    }
    return { queued, total: files.length, target_model: targetModel, results };
  },
};

// ═══════════════════════════════════════════════════════════════
// Training Knowledge Base (FAR/DFARS/policy corpus the Training AI answers from)
// ═══════════════════════════════════════════════════════════════
export const trainingKbApi = {
  /** List KB documents (admin only). */
  list: () => api.get<any>('/training'),
  /** Upload files into the KB. Uploads each file DIRECTLY to S3 via a presigned
   *  URL (bypasses API Gateway's 10MB request limit, so large FAR/DFARS PDFs work),
   *  then triggers background text extraction. */
  uploadFiles: async (category: string, files: File[]) => {
    const results: any[] = [];
    let queued = 0;
    for (const file of files) {
      try {
        const contentType = file.type || 'application/octet-stream';
        // 1. Get a presigned PUT URL.
        const pres = await api.post<any>('/training/presign', {
          filename: file.name, category, content_type: contentType,
        });
        // 2. PUT the file straight to S3. Content-Type MUST match what was signed.
        const put = await fetch(pres.upload_url, {
          method: 'PUT',
          headers: { 'Content-Type': contentType },
          body: file,
        });
        if (!put.ok) throw new Error(`S3 upload failed (${put.status})`);
        // 3. Tell the backend to ingest + extract the uploaded object.
        const ing = await api.post<any>('/training/ingest-s3', {
          s3_key: pres.s3_key, filename: file.name, category,
        });
        results.push({ filename: file.name, status: ing.status || 'processing', id: ing.id });
        queued += 1;
      } catch (e: any) {
        results.push({ filename: file.name, status: 'error', error: e?.message || 'upload failed' });
      }
    }
    return { queued, total: files.length, category, results };
  },
};

export const authApi = {
  me: () => api.get<any>('/auth/me'),
  roles: () => api.get<any>('/auth/roles'),
};

export const usersApi = {
  list: () => api.get<any>('/users'),
  create: (data: any) => api.post<any>('/users', data),
  getGroups: (username: string) => api.get<any>(`/users/${username}/groups`),
  updateGroups: (username: string, data: any) => api.post<any>(`/users/${username}/groups`, data),
  disable: (username: string) => api.post<any>(`/users/${username}/disable`, {}),
  enable: (username: string) => api.post<any>(`/users/${username}/enable`, {}),
  /** Admin: assign/reassign a user's buyside lens (buyer|seller). */
  setOrgType: (email: string, org_type: 'buyer' | 'seller') =>
    api.post<any>(`/users/${email}/org-type`, { org_type }),
};

// ═══════════════════════════════════════════════════════════════
// Intake API
// ═══════════════════════════════════════════════════════════════

export const intakesApi = {
  list: (params?: Record<string, string>) => { const qs = params ? '?' + new URLSearchParams(params).toString() : ''; return api.get<any>(`/intakes${qs}`); },
  get: (id: string) => api.get<any>(`/intakes/${id}`),
  create: (data: any) => api.post<any>('/intakes', data),
  update: (id: string, data: any) => api.put<any>(`/intakes/${id}`, data),
  submit: (id: string) => api.post<any>(`/intakes/${id}/submit`, {}),
  review: (id: string, action: string, comments: string) => api.post<any>(`/intakes/${id}/review`, { action, comments }),
};

// ═══════════════════════════════════════════════════════════════
// Tasks API
// ═══════════════════════════════════════════════════════════════

export const tasksApi = {
  list: (params?: Record<string, string>) => { const qs = params ? '?' + new URLSearchParams(params).toString() : ''; return api.get<any>(`/tasks${qs}`); },
  get: (id: string) => api.get<any>(`/tasks/${id}`),
  create: (data: any) => api.post<any>('/tasks', data),
  update: (id: string, data: any) => api.put<any>(`/tasks/${id}`, data),
  transition: (id: string, status: string, reason?: string) => api.post<any>(`/tasks/${id}/transition`, { status, reason: reason || '' }),
};

// ═══════════════════════════════════════════════════════════════
// Approvals API
// ═══════════════════════════════════════════════════════════════

export const approvalsApi = {
  list: (params?: Record<string, string>) => { const qs = params ? '?' + new URLSearchParams(params).toString() : ''; return api.get<any>(`/approvals${qs}`); },
  get: (id: string) => api.get<any>(`/approvals/${id}`),
  request: (data: any) => api.post<any>('/approvals', data),
  decide: (id: string, decision: string, rationale: string) => api.post<any>(`/approvals/${id}/decide`, { decision, rationale }),
};
